/**
 * Unlock-flow pure helpers (WP-F11a) — the Stripe-return bookkeeping, kept transport-free and
 * unit-testable.
 *
 * The hosted Stripe Checkout success/cancel URLs are STATIC server config (`Stripe:SuccessUrl` /
 * `Stripe:CancelUrl`, WP-B20) — the same for every checkout, so they cannot carry the reference id
 * the redirect belongs to. We therefore remember it ourselves before redirecting, and the return
 * route reads it back to land the candidate on the right reference. `sessionStorage` (not `local`) is
 * deliberate: the pending unlock is scoped to this tab's round-trip and must not leak across tabs or
 * survive a browser restart.
 */

const PENDING_KEY = 'bmw.pendingUnlock';

/** A reference whose paid unlock is mid-round-trip to Stripe. */
export interface PendingUnlock {
  referenceId: string;
  /** When the redirect was initiated (epoch ms) — lets the return route ignore a stale stash. */
  at: number;
}

/** The two outcomes Stripe returns to via the configured success/cancel URLs. */
export type CheckoutReturn = 'success' | 'cancelled';

/** A pending stash older than this is ignored (a forgotten tab reopened days later). */
const PENDING_TTL_MS = 60 * 60 * 1000;

function safeSessionStorage(): Storage | null {
  try {
    return typeof sessionStorage !== 'undefined' ? sessionStorage : null;
  } catch {
    // Access can throw in a sandboxed/blocked context — degrade to "no stash" rather than crash.
    return null;
  }
}

/** Remember which reference a Stripe redirect belongs to, just before navigating away. */
export function stashPendingUnlock(referenceId: string, now: number = Date.now()): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.setItem(PENDING_KEY, JSON.stringify({ referenceId, at: now } satisfies PendingUnlock));
  } catch {
    /* quota / disabled — the return route falls back to the ledger, no crash */
  }
}

/** Read (and validate) the pending unlock, or null when there is none / it is stale / malformed. */
export function readPendingUnlock(now: number = Date.now()): PendingUnlock | null {
  const store = safeSessionStorage();
  if (!store) return null;
  const raw = store.getItem(PENDING_KEY);
  if (raw === null) return null;
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as PendingUnlock).referenceId === 'string' &&
      (parsed as PendingUnlock).referenceId.length > 0 &&
      typeof (parsed as PendingUnlock).at === 'number' &&
      now - (parsed as PendingUnlock).at < PENDING_TTL_MS
    ) {
      return parsed as PendingUnlock;
    }
  } catch {
    /* malformed — treat as absent */
  }
  return null;
}

/** Clear the pending stash (after the return route has consumed it). */
export function clearPendingUnlock(): void {
  const store = safeSessionStorage();
  if (!store) return;
  try {
    store.removeItem(PENDING_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Normalise the `status` the return URL carries into a {@link CheckoutReturn}. The success URL is
 * configured with `?status=success`, the cancel URL with `?status=cancelled`; we also accept the
 * common `cancel`/`canceled` spellings defensively. Anything else → null (not a checkout return).
 */
export function parseCheckoutReturn(value: string | null | undefined): CheckoutReturn | null {
  switch ((value ?? '').toLowerCase()) {
    case 'success':
      return 'success';
    case 'cancel':
    case 'cancelled':
    case 'canceled':
      return 'cancelled';
    default:
      return null;
  }
}

/**
 * Hand the whole page to an external URL (the Stripe hosted-checkout page). The single seam that
 * touches `window.location`, so the redirect is spyable in tests without wrestling jsdom's
 * non-configurable `location.assign`.
 */
export function redirectTo(url: string): void {
  window.location.assign(url);
}
